import { useMemo } from "react";
import { ImportedAccount } from "@common/schemas/domains/accounting/importedAccount.schema";
import { Task } from "@common/types/task";
import { ViewDensity } from "@common/types/views/views";
import { pluralize } from "@common/helpers/string";
import { isImportedAccountInactiveInSource } from "@common/helpers/importedAccountSourceInactivity";
import {
    useBulkUpdateImportedAccounts,
    useImportedAccounts,
} from "@api/accountingIntegration.api";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewConfig } from "@lib/views";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { FinancialAccount } from "@common/types/financialAccount";
import { ImportedAccountCell } from "../shared/ImportedAccountCell";
import { TaskBodyCommandIsland } from "../TaskBodyCommandIsland";
import taskBodyTableStyles from "../TaskBodyTable.module.scss";
import styles from "./MapMigratedDataTaskBody.module.scss";
import { FinancialAccountCell } from "./cells/FinancialAccountCell";
import { ActionsCell } from "./cells/ActionsCell";

interface MapMigratedDataTaskBodyProps {
    selectedTask: Task;
}

enum MapMigratedDataColumn {
    MIGRATED_ACCOUNT = "migratedAccount",
    FINANCIAL_ACCOUNT = "financialAccount",
    ACTIONS = "actions",
}

export interface MapMigratedDataTableMeta {
    financialAccounts: FinancialAccount[];
    onSelectFinancialAccount: (params: {
        importedAccountId: string;
        financialAccountId: number | null;
    }) => void;
    onMarkAsClosed: (params: { importedAccountId: string }) => void;
    onUndoClose: (params: { importedAccountId: string }) => void;
    [key: string]: unknown;
}

const viewConfig: ViewConfig<ImportedAccount, MapMigratedDataColumn, never> = {
    columns: {
        [MapMigratedDataColumn.MIGRATED_ACCOUNT]: {
            accessorKey: "name",
            name: "Migrated account",
            header: "Migrated account",
            immutable: true,
            minSize: 200,
            maxSize: 1200,
            cell: ImportedAccountCell,
        },
        [MapMigratedDataColumn.FINANCIAL_ACCOUNT]: {
            accessorKey: "mappedFinancialAccountId",
            name: "Financial account",
            header: "Financial account",
            immutable: true,
            minSize: 200,
            maxSize: 1200,
            cell: FinancialAccountCell,
        },
        [MapMigratedDataColumn.ACTIONS]: {
            accessorKey: "id",
            name: "",
            header: "Actions",
            immutable: true,
            hiddenFromSettings: true,
            minSize: 48,
            maxSize: 80,
            cell: ActionsCell,
        },
    },
};

const isAutoClosedAccount = (account: ImportedAccount): boolean =>
    account.isClosed && isImportedAccountInactiveInSource(account);

// Rows pre-closed from source inactivity need no action, so they trail the
// rows the accountant still has to link. Manually closed rows keep their
// place so a click does not move the row under the cursor.
const sortAutoClosedAccountsLast = (
    accounts: ImportedAccount[],
): ImportedAccount[] => [
    ...accounts.filter((account) => !isAutoClosedAccount(account)),
    ...accounts.filter(isAutoClosedAccount),
];

const view = {
    filters: {},
    columns: [
        { id: MapMigratedDataColumn.MIGRATED_ACCOUNT },
        { id: MapMigratedDataColumn.FINANCIAL_ACCOUNT },
        { id: MapMigratedDataColumn.ACTIONS },
    ],
    options: {
        density: ViewDensity.MD,
    },
};

export const MapMigratedDataTaskBody = ({
    selectedTask,
}: MapMigratedDataTaskBodyProps) => {
    const entityId = selectedTask.entityIds?.[0];

    const { data: importedAccountsData, isLoading } = useImportedAccounts(
        selectedTask.workspaceId,
        entityId,
        { requiresFinancialAccountMapping: true, orderBy: "institution" },
    );
    const importedAccounts = importedAccountsData?.accounts;

    const financialAccounts = useFinancialAccounts({ entityId });

    const { mutate: bulkUpdate } = useBulkUpdateImportedAccounts(
        selectedTask.workspaceId,
        entityId!,
    );

    const tableMeta: MapMigratedDataTableMeta = useMemo(
        () => ({
            financialAccounts,
            onSelectFinancialAccount: ({
                importedAccountId,
                financialAccountId,
            }) => {
                bulkUpdate({
                    updates: [
                        {
                            importedAccountId,
                            mappedFinancialAccountId: financialAccountId,
                        },
                    ],
                });
            },
            onMarkAsClosed: ({ importedAccountId }) => {
                bulkUpdate({
                    updates: [
                        {
                            importedAccountId,
                            isClosed: true,
                            mappedFinancialAccountId: null,
                        },
                    ],
                });
            },
            onUndoClose: ({ importedAccountId }) => {
                bulkUpdate({
                    updates: [
                        {
                            importedAccountId,
                            isClosed: false,
                        },
                    ],
                });
            },
        }),
        [financialAccounts, bulkUpdate],
    );

    const accounts = useMemo(
        () => sortAutoClosedAccountsLast(importedAccounts ?? []),
        [importedAccounts],
    );

    if (!entityId || isLoading) {
        return null;
    }

    const count = accounts.length;
    const subtitle = `${count} ${pluralize("account", count)} to map`;

    return (
        <div className={styles.container}>
            <div className={styles.subtitle}>{subtitle}</div>

            <ViewTable
                data={accounts}
                view={view}
                config={viewConfig}
                className={taskBodyTableStyles.viewTableWithoutTopBorder}
                rowBorders
                columnBorders
                enableColumnResizing={false}
                tableMeta={tableMeta}
                emptyView={() => <span>No accounts to map.</span>}
                footer={<TaskBodyCommandIsland selectedTask={selectedTask} />}
            />
        </div>
    );
};
